import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react'

import {
  Button,
  ButtonSentiment,
  ComboboxControlled,
  Icon,
} from 'sidecar-components'
import { PreCheckedProviders as PreCheckedProvidersComponent } from 'sidecar-member-components'

// React Router Link wrapper that matches the GenericLinkComponent interface
const ReactRouterLinkComponent = ({
  children,
  href,
  to,
  className,
  'data-testid': testId,
  target,
  rel,
  ...props
}) => {
  const url = href || to
  if (!url) return <span className={className}>{children}</span>

  // For external links, use regular anchor tag
  if (url.startsWith('http')) {
    return (
      <a
        href={url}
        className={className}
        data-testid={testId}
        target={target || '_blank'}
        rel={rel || 'noopener noreferrer'}
        {...props}
      >
        {children}
      </a>
    )
  }

  // For internal links, just use anchor since this app doesn't seem to use Link from react-router
  return (
    <a href={url} className={className} data-testid={testId} {...props}>
      {children}
    </a>
  )
}

import { providerCategoriesQuery } from './query'
import { formTitles } from 'utils/memberComm/formTitles'

export const PreCheckedProviders = ({
  contentfulBusy = true,
  contentfulError,
  testId,
  fetchMemberCommsPrecheckedCategories,
  clearMemberCommsPrecheckedCategories,
  preCheckedProviders = [],
  preCheckedProvidersBusy,
  preCheckedProvidersError,
  fetchMemberCommsPrecheckedProviders,
  clearMemberCommsPrecheckedProviders,
  ratingArea,
  setPoptartHistory,
  setSelectedSpecialty,
  specialtyFields,
}) => {
  // Get the request form title from the enum to route the user in setPoptartHistory
  const { REQUEST_FORM } = formTitles

  // State to handle if the specialty fields were fetched
  const [specialtyFieldFetched, setSpecialtyFieldFetched] = useState(false)
  // State to store selected specialty field
  const [specialty, setSpecialty] = useState('')

  // State for tracking selected specialty
  const [selectedSpecialtyValue, setSelectedSpecialtyValue] =
    useState(undefined)

  // State for controlling the specialty dropdown's open state and typed input
  const [isSpecialtyOpen, setIsSpecialtyOpen] = useState(false)
  const [specialtyInputValue, setSpecialtyInputValue] = useState('')

  // ComboboxControlled writes its raw option value (not label) into the input
  // on select, since it fires after onSelect completes. Re-sync to the label
  // before paint so the field shows "Cardiology" instead of the raw option
  // value it's built from, e.g. "Specialty care - cardiology::Cardiology".
  useLayoutEffect(() => {
    if (selectedSpecialtyValue) {
      setSpecialtyInputValue(selectedSpecialtyValue.label)
    }
  }, [selectedSpecialtyValue])

  // Fetch the specialty fields that will be displayed in the Select
  useEffect(() => {
    // Reset the selected specialty to prevent it from persisting
    setSelectedSpecialty('')
    setSpecialty('')
    setSpecialtyFieldFetched(true)
    const query = `query GetContent {${providerCategoriesQuery()}} `
    fetchMemberCommsPrecheckedCategories(query)

    return () => {
      clearMemberCommsPrecheckedCategories()
      clearMemberCommsPrecheckedProviders()
    }
  }, [])

  // Build the options to be compatible with the dropdown component
  const specialtyFieldOptions = useMemo(() => {
    const options = []
    const prefix = 'Pre-checked providers - '

    if (specialtyFields && Array.isArray(specialtyFields)) {
      specialtyFields.forEach(field => {
        if (field.preCheckedProviderCategories.length > 1) {
          field.preCheckedProviderCategories.forEach((category, i) => {
            options.push({
              label: category,
              value: `${field.preCheckedProviderCategories[i]}::${category}`,
            })
          })
        } else {
          const name = field.name.replace(prefix, '')
          options.push({
            label: name,
            value: `${field.preCheckedProviderCategories[0]}::${name}`,
          })
        }
      })
    }

    options.push({
      label: 'Other',
      value: 'other::Other',
    })
    options.sort((a, b) => a.label.localeCompare(b.label))

    return options
  }, [specialtyFields])

  // State used to handle routing the user to the request form
  const [preCheckedFetched, setPreCheckedFetched] = useState(false)

  // When the user selects a specialty, fetch the preChecked providers
  const handleSelectSpecialty = async formValue => {
    setPreCheckedFetched(false)
    const formValueArr = formValue.value?.split(/::/)
    const result = formValueArr[0]
    const selection = formValueArr[1]

    setSpecialty(selection)
    setSelectedSpecialty(selection)

    if (result.toLowerCase() === 'other') {
      // Reset prechecked providers to prevent them from persisting if a user
      // selects a specialty with pre-checked providers before selecting a
      // specialty with a value of other
      clearMemberCommsPrecheckedProviders()
      // Route the user to the request form without showing pre-checked providers
      setPoptartHistory(REQUEST_FORM)
    } else {
      await fetchMemberCommsPrecheckedProviders(result, ratingArea)
      setPreCheckedFetched(true)
    }
  }

  // Check if the specialties and providers were fetched but the response is
  // empty or if there is an error
  const noSpecialties =
    (specialtyFields.length === 0 &&
      specialtyFieldFetched &&
      !contentfulBusy) ||
    contentfulError
  const noProviders =
    (preCheckedProviders.length === 0 &&
      preCheckedFetched &&
      !preCheckedProvidersBusy) ||
    preCheckedProvidersError

  // This useEffect is needed to route the user to the request form where there
  // are no prechecked providers
  useEffect(() => {
    if (noProviders || noSpecialties) {
      clearMemberCommsPrecheckedCategories()
      clearMemberCommsPrecheckedProviders()
      setPoptartHistory(REQUEST_FORM)
    }
  }, [noProviders, noSpecialties])

  return (
    <div data-testid={testId} id="request_form">
      <>
        <form
          className="max-w-6xl w-full mx-auto gap-3 block"
          data-testid="info_selectSpecialty"
        >
          {/* Specialty dropdown */}
          <h4>What kind of care do you need?</h4>
          <ComboboxControlled
            // don't allow users to submit a partial value
            autoSelect={true}
            testId="info_specialtyFieldsDropdown"
            disabled={contentfulBusy || specialtyFieldOptions.length === 0}
            id="specialty_fields_dropdown"
            label="Specialty"
            name="specialty"
            selectedValue={selectedSpecialtyValue}
            onSelect={selection => {
              setSelectedSpecialtyValue(selection)
              handleSelectSpecialty(selection)
            }}
            items={specialtyFieldOptions}
            placeholder="Start typing..."
            wrapperClass="mb-5"
            searchable={true}
            open={isSpecialtyOpen}
            onOpenChange={setIsSpecialtyOpen}
            value={specialtyInputValue}
            setValue={setSpecialtyInputValue}
          />
        </form>

        {/* PreChecked provider section */}
        {preCheckedProviders.length > 0 && !contentfulBusy && (
          <>
            <PreCheckedProvidersComponent
              className="my-6"
              content={{
                // Duplicate providers are filtered the care store
                providers: preCheckedProviders,
                sidecarCodeType: 'Professional',
              }}
              type="requestForm"
              preCheckedCategoryName={specialty}
              linkComponent={ReactRouterLinkComponent}
            />
            <h4>Still looking for something else?</h4>
            <Button
              onClick={() => setPoptartHistory(REQUEST_FORM)}
              sentiment={ButtonSentiment.SECONDARY}
              size="small"
              width="full"
              className="my-1"
            >
              Get additional help
            </Button>
          </>
        )}

        {/* Busy state */}
        {(!!contentfulBusy || !!preCheckedProvidersBusy) && (
          <div
            className="flex justify-center items-center"
            data-testid="info_busyState"
          >
            <Icon
              name="loading"
              className="animate-spin inherit"
              size="xl"
              testId="icon_loading"
              pathClass="color-icon-default"
            />
          </div>
        )}
      </>
    </div>
  )
}
